import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

type StoredToken = {
  AccessToken?: string;
};

/**
 * One entry of `results[]` on a PATCH response (see PAMENG-1776). Returned per
 * referrer-bonus banner the cancellation step touched when the operator
 * toggles the user from "allowed" to "blocked".
 *
 * - `cancelled` — banner cancelled by this request.
 * - `skipped`   — row already in a terminal state (e.g. user claimed it
 *                 first); not an error.
 * - `failed`    — write error on this row; the overall PATCH then surfaces
 *                 `REFERRER_BONUS_CANCELLATION_PARTIAL_FAILURE`.
 */
export type ReferrerBonusResultRow = {
  bonus_id: string;
  outcome: "cancelled" | "skipped" | "failed";
};

/**
 * Shape returned by both GET and PATCH on
 * `/admin-panel-users/v1/{user_id}/referrer-bonus/enabled`.
 *
 * - GET — `referrer_bonus_enabled` reflects the current flag. A user record
 *   without the attribute reads as `true` (never-toggled users are allowed).
 *   `cancelled_count` / `skipped_count` / `results` are absent on GET (the
 *   BE returns 0 / 0 / []).
 * - PATCH — same fields; on a successful block (`enabled: true → false`)
 *   `cancelled_count` / `skipped_count` / `results` are populated.
 */
export type ReferrerBonusEnabledResponse = {
  referrer_bonus_enabled: boolean;
  success?: boolean;
  msg_code?: string | null;
  format_args?: Record<string, unknown> | null;
  cancelled_count?: number;
  skipped_count?: number;
  results?: ReferrerBonusResultRow[];
} | null;

const fetchReferrerBonusEnabled = async (
  user_id: string,
): Promise<ReferrerBonusEnabledResponse> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const response = await PrivateAdminPanelInstance.get(
    API_ROUTES.USER_REFERRER_BONUS_ENABLED.replace("{user_id}", user_id),
  );

  return response?.data ?? null;
};

export const useGetReferrerBonusEnabled = (
  user_id?: string | null,
  enabled: boolean = true,
) =>
  useQuery<ReferrerBonusEnabledResponse, Error>({
    queryKey: ["referrerBonusEnabled", user_id],
    queryFn: () => fetchReferrerBonusEnabled(user_id as string),
    enabled: Boolean(user_id) && enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
